import { useRef } from "react";
import { Check, Plus, X } from "lucide-react";
import { ExtractedColorPalette } from '@/types/ui/branding';
import { cn } from "@/lib/utils";

interface ColorPaletteStripProps {
  extractedColors: ExtractedColorPalette | null;
  onColorSelect: (color: string) => void;
  selectedColor?: string;
  label?: string;
  /** Enables adding a custom swatch and removing existing ones. Omit for a read-only strip. */
  onPaletteChange?: (palette: string[]) => void;
}

export const ColorPaletteStrip = ({
  extractedColors,
  onColorSelect,
  selectedColor,
  label = "Your brand colors",
  onPaletteChange,
}: ColorPaletteStripProps) => {
  const addColorInputRef = useRef<HTMLInputElement>(null);

  if (!extractedColors) return null;

  const isSelected = (color: string) => {
    return color.toLowerCase() === selectedColor?.toLowerCase();
  };

  const handleAddColor = (color: string) => {
    if (!onPaletteChange) return;
    onPaletteChange([...extractedColors.palette, color]);
    onColorSelect(color);
  };

  const handleRemoveColor = (index: number) => {
    if (!onPaletteChange) return;
    onPaletteChange(extractedColors.palette.filter((_, i) => i !== index));
  };

  return (
    <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
      <p className="text-xs text-gray-600 mb-2">{label}</p>
      <div className="flex flex-wrap gap-2">
        {extractedColors.palette.map((color, index) => (
          <button
            key={`${color}-${index}`}
            type="button"
            onClick={() => onColorSelect(color)}
            className={cn(
              "relative group w-10 h-10 rounded-md border-2 transition-all flex-shrink-0",
              isSelected(color)
                ? "border-blue-500 ring-2 ring-blue-200 scale-110"
                : "border-gray-300 hover:border-gray-400 hover:scale-105"
            )}
            style={{ backgroundColor: color }}
            title={color}
          >
            {isSelected(color) && (
              <div className="absolute inset-0 bg-black/20 flex items-center justify-center rounded-sm">
                <Check className="w-4 h-4 text-white" />
              </div>
            )}
            {onPaletteChange && extractedColors.palette.length > 1 && (
              <span
                role="button"
                tabIndex={0}
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemoveColor(index);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.stopPropagation();
                    e.preventDefault();
                    handleRemoveColor(index);
                  }
                }}
                className="absolute -top-1.5 -right-1.5 bg-gray-700 text-white rounded-full w-4 h-4 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-500"
                title="Remove color"
              >
                <X className="w-2.5 h-2.5" />
              </span>
            )}
            <span className="absolute -top-8 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
              {color}
            </span>
          </button>
        ))}
        {onPaletteChange && (
          <button
            type="button"
            onClick={() => addColorInputRef.current?.click()}
            className="relative w-10 h-10 rounded-md border-2 border-dashed border-gray-300 hover:border-gray-400 flex items-center justify-center flex-shrink-0 text-gray-400 hover:text-gray-600 transition-colors"
            title="Add a custom color"
          >
            <Plus className="w-4 h-4" />
            <input
              ref={addColorInputRef}
              type="color"
              className="absolute inset-0 opacity-0 cursor-pointer"
              onChange={(e) => handleAddColor(e.target.value)}
              tabIndex={-1}
            />
          </button>
        )}
      </div>
    </div>
  );
};
